import './GraphLayoutDialog.css';

import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import {
  DEFAULT_GRAPH_LAYOUT,
  type Layout,
  isNonNullable,
  narrowType,
} from '@ipfabric/utils';
import { maybePropOf, prop } from '@ipfabric/utils/fp/prop';
import { isTopologyGraphMetadata } from 'components/Graph/GraphState';
import { useLocale } from 'containers/App/LocaleContext';
import { option } from 'fp-ts';
import { flow, pipe } from 'fp-ts/lib/function';
import { type FC, memo, useState } from 'react';
import { useGraphContextSelectors } from '../GraphContext';
import { GraphLayout } from './GraphLayout';
import type { GraphLayoutChangeEvent } from './GraphLayoutItem';

import { useClickAway } from '@ipfabric/ui-components/hooks/useClickAway';
import { hideGraphLayoutDialog } from 'components/Graph/commands/hideGraphLayoutDialog';
import { layoutPath } from 'components/Graph/commands/layouts/layoutPath';

const BareGraphLayoutDialog: FC = () => {
  const msg = useLocale();
  const ctxProps = useGraphContextSelectors({
    dispatch: prop('dispatch'),
    // this component is rendered only when graph layout dialog is present in state
    graphLayoutDialog: flow(
      prop('graphLayoutDialog'),
      narrowType(isNonNullable),
    ),
    snapshotId: prop('snapshotId'),
    // graph layout dialog can be open only in context of a topology graph
    topologyMetadata: flow(
      prop('graphMetadata'),
      narrowType(isTopologyGraphMetadata),
    ),
  });
  const { path } = ctxProps.graphLayoutDialog;
  const unsavedUserDefined =
    ctxProps.topologyMetadata.nodesRepositioned.has(path);

  const [activeLayout, setActiveLayout] = useState<Layout | null>(() => {
    if (ctxProps.graphLayoutDialog.nodeSns !== undefined) {
      return null;
    }

    if (unsavedUserDefined) {
      return 'userDefined';
    }

    return pipe(
      path,
      maybePropOf(ctxProps.topologyMetadata.paths),
      option.fold(() => {
        stumble(`No layout info loaded for path ${path}`);
        return DEFAULT_GRAPH_LAYOUT;
      }, prop('defaultLayout')),
    );
  });

  const handleLayoutChange = useStableCallback(
    async (event: GraphLayoutChangeEvent) => {
      // Select new layout if selected option is different to current option.
      if (event.layout !== activeLayout) {
        // Show switched layout until layout is saved and dialog is closed.
        setActiveLayout(event.layout);
        await ctxProps.dispatch(layoutPath, {
          layoutRequest: {
            nodeSns: ctxProps.graphLayoutDialog.nodeSns,
            layout: event.layout,
            path,
          },
        });
      }

      ctxProps.dispatch(hideGraphLayoutDialog);
    },
  );

  const handleSaveUserDefined = useStableCallback(() => {
    ctxProps.dispatch(hideGraphLayoutDialog);
  });

  const dialogRef = useClickAway<HTMLDivElement>(() => {
    ctxProps.dispatch(hideGraphLayoutDialog);
  });

  return (
    <div className='graph-layout-dialog' ref={dialogRef}>
      <div className='p-5 font-bold'>
        {msg.canvasGraph.graphLayoutDialog.title}
      </div>
      <GraphLayout
        activeLayout={activeLayout}
        groupBy={ctxProps.topologyMetadata.parameters.groupBy}
        layoutItemClassName='graph-layout-dialog__item px-5'
        onChange={handleLayoutChange}
        path={path}
        onSaveUserDefined={handleSaveUserDefined}
        showSaveUserDefined={unsavedUserDefined}
      />
    </div>
  );
};

export const GraphLayoutDialog = memo(BareGraphLayoutDialog);
